
import { useState, useCallback, useMemo } from "react";
import { ContractService } from "@/lib/types/services";

export interface ServiceChange {
  type: 'add' | 'update' | 'remove';
  data: any;
}

export interface ServicesManagementState {
  servicesTotal: number;
  temporaryServices: ContractService[];
  pendingChanges: Map<string, ServiceChange>;
  hasChanges: boolean;
}

export function useServicesManagement() {
  const [servicesTotal, setServicesTotal] = useState<number>(0);
  const [temporaryServices, setTemporaryServices] = useState<ContractService[]>([]);
  const [pendingChanges, setPendingChanges] = useState<Map<string, ServiceChange>>(new Map());
  
  // Memoize the hasChanges value to prevent unnecessary rerenders
  const hasChanges = useMemo(() => pendingChanges.size > 0, [pendingChanges]);
  
  const handleServicesTotalUpdate = useCallback((total: number) => {
    setServicesTotal(total);
  }, []);
  
  const addPendingChange = useCallback((id: string, change: ServiceChange) => {
    setPendingChanges(prev => {
      const updated = new Map(prev);
      updated.set(id, change);
      return updated;
    });
  }, []);
  
  const removePendingChange = useCallback((id: string) => {
    setPendingChanges(prev => {
      const updated = new Map(prev);
      updated.delete(id);
      return updated;
    });
  }, []);
  
  const clearPendingChanges = useCallback(() => {
    setPendingChanges(new Map());
  }, []);
  
  const addTemporaryService = useCallback((service: ContractService) => {
    setTemporaryServices(prev => [...prev, service]);
  }, []);
  
  const updateTemporaryService = useCallback((id: string, data: Partial<ContractService>) => {
    setTemporaryServices(prev => 
      prev.map(s => s.id === id ? { ...s, ...data } : s)
    );
  }, []);
  
  const removeTemporaryService = useCallback((id: string) => {
    setTemporaryServices(prev => prev.filter(s => s.id !== id));
  }, []);
  
  return {
    servicesTotal,
    temporaryServices,
    pendingChanges,
    hasChanges,
    handleServicesTotalUpdate,
    addPendingChange,
    removePendingChange,
    clearPendingChanges,
    addTemporaryService,
    updateTemporaryService,
    removeTemporaryService
  };
}
